/**
 * Centralized screen registrations.
 *
 * All registerScreen() calls live here. Individual screen files export their
 * component (named + default) but no longer call registerScreen themselves.
 *
 * Currently all imports are eager because screen files still register window
 * globals used by services (PubNub callbacks, keyboard shortcuts, etc.).
 * Once Phase 17 removes those window registrations, these can be converted
 * to React.lazy(() => import(...)) for code splitting.
 */

import { registerScreen } from './screen-registry';

// ── Screen imports (eager — window registrations must execute at load) ──

import { HomeScreen } from '@/components/screens/HomeScreen';
import { SelectModuleScreenWrapper } from '@/components/screens/SelectModuleScreen';
import { OfficerStatusScreen } from '@/components/screens/OfficerStatusScreen';
import { InProgressScreen } from '@/components/screens/InProgressScreen';
import { NotesScreen } from '@/components/screens/NotesScreen';
import { CitationsScreen } from '@/components/screens/CitationsScreen';
import { PermissionsScreen } from '@/components/screens/PermissionsScreen';
import { GuardToursScreen } from '@/components/screens/GuardToursScreen';
import { RegistrationScreen } from '@/components/screens/RegistrationScreen';
import { ResetPasswordScreen } from '@/components/screens/ResetPasswordScreen';
import { UnsubscribeScreen } from '@/components/screens/UnsubscribeScreen';
import { MessagingScreen } from '@/components/screens/MessagingScreen';
import { CompanySettingsScreen } from '@/components/screens/CompanySettingsScreen';
import { CompanyProfileScreen } from '@/components/screens/CompanyProfileScreen';
import { CompanyLinkScreen } from '@/components/screens/CompanyLinkScreen';
import { UserProfileScreen } from '@/components/screens/UserProfileScreen';
import { PaymentManagementScreen } from '@/components/screens/PaymentManagementScreen';
import { ClientPortalAdminScreen } from '@/components/screens/ClientPortalAdminScreen';
import { CaseReportManagementScreen } from '@/components/screens/CaseReportManagementScreen';
import { CaseReportListScreen } from '@/components/screens/CaseReportListScreen';
import { CaseReportFormScreen } from '@/components/screens/CaseReportFormScreen';
import { CitationFormScreen } from '@/components/screens/CitationFormScreen';
import { CitationManagementScreen } from '@/components/screens/CitationManagementScreen';
import { NocManagementScreen } from '@/components/screens/NocManagementScreen';
import { CallCardManagementScreen } from '@/components/screens/CallCardManagementScreen';
import { CallAlertManagementScreen } from '@/components/screens/CallAlertManagementScreen';
import { AdvisoryManagementScreen } from '@/components/screens/AdvisoryManagementScreen';
import { AdvisoryScreen } from '@/components/screens/advisory/AdvisoryScreen';
import { CustomLocationManagementScreen } from '@/components/screens/CustomLocationManagementScreen';
import { SupervisorAddressReviewScreen } from '@/components/screens/SupervisorAddressReviewScreen';
import { UserManagementScreen } from '@/components/screens/UserManagementScreen';
import { CallSignManagementScreen } from '@/components/screens/CallSignManagementScreen';
import { StatusUpdateOptionManagementScreen } from '@/components/screens/StatusUpdateOptionManagementScreen';
import { CompanyDocsScreen } from '@/components/screens/CompanyDocsScreen';
import { OrnaSupportAttachmentsScreen } from '@/components/screens/OrnaSupportAttachmentsScreen';
import { MapOverlayKmlScreen } from '@/components/screens/MapOverlayKmlScreen';
import { MapScreen } from '@/components/screens/MapScreen';
import { VehicleManagementScreen } from '@/components/screens/VehicleManagementScreen';
import { TourManagementScreen } from '@/components/screens/TourManagementScreen';
import { ActivityReportScreen } from '@/components/screens/ActivityReportScreen';
import { ActivityReportManagementScreen } from '@/components/screens/ActivityReportManagementScreen';
import { AnalyticsReportScreen } from '@/components/screens/AnalyticsReportScreen';
import { EmergencyLookupScreen } from '@/components/screens/EmergencyLookupScreen';
import { LogScreen } from '@/components/screens/LogScreen';
import { CallCardScreenWrapper } from '@/components/screens/CallCardScreen';
import { ClientGroupManagementScreen } from '@/components/screens/ClientGroupManagementScreen';
import { SopScreen } from '@/components/screens/SopScreen';
import { TourStopCompletionScreenWrapper } from '@/components/screens/TourStopCompletionScreen';
import { CustomFormsScreen } from '@/components/screens/CustomFormsScreen';

// ── NPSD screen imports ──

import { NPSDSearch } from '@/components/npsd/NPSDSearch';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';

import { App } from '@/services/app-compat';
import { AccessAuthorities } from '@/models/access-authorities';

// ── Window globals for getProps ──

const HelpDeskType = (window as any).HelpDeskType;
import { getCurrentUserId } from '@/utils/auth';

// ── Screen registrations ──

// Home & navigation
registerScreen('homeScreen', {
  component: HomeScreen,
  getProps: (_params, section) => {
    const sectionUpper = (section || '').toUpperCase();
    let moduleTitle = '';
    let moduleShortTitle = '';
    let helpDeskType = null;
    if (sectionUpper === 'ADMIN') {
      moduleTitle = 'Administration Module';
      moduleShortTitle = 'Admin';
      helpDeskType = HelpDeskType?.AdminGettingStarted ?? null;
    } else if (sectionUpper === 'CAD') {
      moduleTitle = 'Dispatch Module';
      moduleShortTitle = 'Dispatch';
      helpDeskType = HelpDeskType?.CADGettingStarted ?? null;
    } else if (sectionUpper === 'MDT') {
      moduleTitle = 'Mobile Patrol Module';
      moduleShortTitle = 'Mobile';
      helpDeskType = HelpDeskType?.MDTGettingStarted ?? null;
    }
    return { moduleTitle, moduleShortTitle, helpDeskType };
  },
});

registerScreen('selectModule', { component: SelectModuleScreenWrapper });

registerScreen('registration', {
  component: RegistrationScreen,
  getProps: (params) => ({ code: params.code || undefined }),
});

registerScreen('resetPassword', {
  component: ResetPasswordScreen,
  getProps: (params) => ({ code: params.code || undefined }),
});

registerScreen('unsubscribe', { component: UnsubscribeScreen });

// Multi-module screens
registerScreen('OfficerStatus', {
  component: OfficerStatusScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
  }),
});

registerScreen('InProgress', {
  component: InProgressScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
    displayMode: 'main' as const,
  }),
});

registerScreen('InProgressPendingCardsFullScreen', {
  component: InProgressScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
    displayMode: 'fullscreen-pending' as const,
  }),
});

registerScreen('InProgressActiveCardsFullScreen', {
  component: InProgressScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
    displayMode: 'fullscreen-active' as const,
  }),
});

registerScreen('InProgressClosedCardsFullScreen', {
  component: InProgressScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
    displayMode: 'fullscreen-closed' as const,
  }),
});

registerScreen('InProgressPanic', {
  component: InProgressScreen,
  getProps: (_params, section) => ({
    moduleType: section.toLowerCase() as 'cad' | 'mdt',
    displayMode: 'fullscreen-panic' as const,
  }),
});

registerScreen('Notes', {
  component: NotesScreen,
  requiredPermission: AccessAuthorities.ROLE_ORNA_NOTES_READ,
});

registerScreen('Citations', {
  component: CitationsScreen,
  getProps: (_params, section) => ({
    moduleType: section,
  }),
});

registerScreen('Messaging', { component: MessagingScreen });
registerScreen('Log', { component: LogScreen });
registerScreen('EmergencyLookup', { component: EmergencyLookupScreen });
registerScreen('ActivityReport', { component: ActivityReportScreen });
registerScreen('AnalyticsReport', { component: AnalyticsReportScreen });
registerScreen('Search', { component: NPSDSearch });
registerScreen('MapOverlayKml', { component: MapOverlayKmlScreen });
registerScreen('ADMIN_MapOverlayKml', { component: MapOverlayKmlScreen });

registerScreen('CAD_Map', {
  component: MapScreen,
  getProps: () => ({ moduleType: 'cad' }),
});
registerScreen('MDT_Map', {
  component: MapScreen,
  getProps: () => ({ moduleType: 'mdt' }),
});
registerScreen('CAD_MapById', {
  component: MapScreen,
  getProps: (params) => ({ moduleType: 'cad', callCardId: params.callCardId }),
});
registerScreen('MDT_MapById', {
  component: MapScreen,
  getProps: (params) => ({ moduleType: 'mdt', callCardId: params.callCardId }),
});
registerScreen('NPSD_MapByAddress', {
  component: MapScreen,
  getProps: (params) => ({ moduleType: 'mdt', address: params.address }),
});

registerScreen('UserProfile', {
  component: UserProfileScreen,
  getProps: (params) => ({
    userId: params.id || getCurrentUserId().toString(),
  }),
});

registerScreen('CaseReports', {
  component: CaseReportListScreen,
  getProps: () => ({
    isMobile: App.isMobile(),
  }),
});

// Guard tours
registerScreen('GuardToursNew', { component: GuardToursScreen });

registerScreen('showCompleteTourStopsForGuardTour', {
  component: GuardToursScreen,
  getProps: (params) => ({
    guardTourId: params.guardTourId ? parseInt(params.guardTourId) : null,
    guardTourStopId: params.guardTourStopId ? parseInt(params.guardTourStopId) : null,
  }),
});

// Call card
registerScreen('CAD_CallCard', {
  component: CallCardScreenWrapper,
  getProps: (params, _section, navState) => ({
    callCardId: params.id ? parseInt(params.id) : undefined,
    moduleType: 'cad',
    mapLat: navState?.lat,
    mapLng: navState?.lng,
    customLocationName: navState?.customLocationName,
  }),
});

registerScreen('MDT_ShowCallCard', {
  component: CallCardScreenWrapper,
  getProps: (params) => ({
    callCardId: params.id ? parseInt(params.id) : undefined,
    moduleType: 'mdt',
  }),
});

// Case reports
registerScreen('displayCaseReport', {
  component: CaseReportFormScreen,
  getProps: (params, _section, state) => {
    return {
      reportId: params.params || undefined,
      isLog: state?.isLog,
      viewOnly: state?.viewOnly ?? false,
      currentTab: state?.currentTab || undefined,
    };
  },
});

// Tour Stop Completion
registerScreen('TourStopCompletion', {
  component: TourStopCompletionScreenWrapper,
  getProps: (params) => ({
    completionId: params.id ? parseInt(params.id) : (params.params ? parseInt(params.params) : undefined),
  }),
});

// Citations
registerScreen('Open_CitationForm', {
  component: CitationFormScreen,
  getProps: (params) => {
    const parts = (params.params || 'new').split(',');
    const mode = parts[0] || 'new';
    const preFilledPersonId = parts[1] ? parseInt(parts[1], 10) : undefined;
    const preLinkedCallCardId = parts[2] ? parseInt(parts[2], 10) : undefined;
    const citationId = parts[3] ? parseInt(parts[3], 10) : undefined;

    return {
      options: {
        mode: mode as 'new' | 'edit' | 'view',
        citationId,
        preFilledPersonId,
        preLinkedCallCardId,
        parentContext: (window as any).citationParentContext || undefined,
        parentId: (window as any).citationParentId || undefined,
      },
    };
  },
});

// NPSD entity screens
registerScreen('Open_Person', {
  component: NPSDPersonScreen,
  getProps: (params) => ({
    personId: params.id || null,
  }),
});

registerScreen('Open_Vehicle', {
  component: NPSDVehicleScreen,
  getProps: (params) => ({
    vehicleId: params.id || null,
  }),
});

registerScreen('Open_Business', {
  component: NPSDBusinessScreen,
  getProps: (params) => ({
    businessId: params.id || null,
  }),
});

registerScreen('Open_ClientGroup', {
  component: NPSDClientScreenWrapper,
  getProps: (params) => ({
    clientId: params.id || null,
  }),
});

registerScreen('Open_AdminClientGroup', {
  component: NPSDClientScreenWrapper,
  getProps: (params) => ({
    clientId: params.id || null,
    isAdmin: true,
  }),
});

// Admin screens
registerScreen('ADMIN_Permissions', { component: PermissionsScreen });
registerScreen('ADMIN_CompanySettings', { component: CompanySettingsScreen });
registerScreen('ADMIN_CompanyProfile', { component: CompanyProfileScreen });
registerScreen('ADMIN_CompanyLink', { component: CompanyLinkScreen });
registerScreen('ADMIN_CaseReportManagement', { component: CaseReportManagementScreen });
registerScreen('ADMIN_CitationManagement', { component: CitationManagementScreen });
registerScreen('ADMIN_NOCManagement', { component: NocManagementScreen });
registerScreen('ADMIN_CallCardManagement', { component: CallCardManagementScreen });
registerScreen('ADMIN_UserManagement', { component: UserManagementScreen });
registerScreen('ADMIN_CallSignManagement', { component: CallSignManagementScreen });
registerScreen('ADMIN_StatusUpdateManagement', { component: StatusUpdateOptionManagementScreen });
registerScreen('ADMIN_VehicleManagement', { component: VehicleManagementScreen });
registerScreen('ADMIN_OrnaSupportAttachments', { component: OrnaSupportAttachmentsScreen });
registerScreen('ADMIN_ActivityReportManagement', { component: ActivityReportManagementScreen });
registerScreen('ADMIN_CustomFormsManagement', { component: CustomFormsScreen });
registerScreen('TourManagement', {
  component: TourManagementScreen,
  getProps: (params) => ({ initialSubTab: params.subTab }),
});
registerScreen('PaymentManagement', { component: PaymentManagementScreen });
registerScreen('PAYMENT_PaymentManagement', { component: PaymentManagementScreen });
registerScreen('ADMIN_PaymentManagement', { component: PaymentManagementScreen });
registerScreen('ClientPortal', { component: ClientPortalAdminScreen });
registerScreen('ADMIN_ClientGroupManagement', { component: ClientGroupManagementScreen });

// Multi-module admin screens (same component, different registry keys)
registerScreen('CAD_CallAlertManagement', { component: CallAlertManagementScreen });
registerScreen('MDT_CallAlertManagement', { component: CallAlertManagementScreen });
registerScreen('ADMIN_CallAlertManagement', { component: CallAlertManagementScreen });

// Admin advisory configuration (Advisory Types + Audit Log tabs only)
registerScreen('ADMIN_AdvisoryManagement', { component: AdvisoryManagementScreen });

// Operational advisory screen — single adaptive surface on both CAD and MDT.
// Read-only users see cards; editors get add/edit/delete/restore affordances.
registerScreen('CAD_Advisories', {
  component: AdvisoryScreen,
  requiredPermission: AccessAuthorities.ROLE_ORNA_ADVISORY_READ,
});
registerScreen('MDT_Advisories', {
  component: AdvisoryScreen,
  requiredPermission: AccessAuthorities.ROLE_ORNA_ADVISORY_READ,
});

registerScreen('CAD_CustomLocationManagement', { component: CustomLocationManagementScreen });
registerScreen('MDT_CustomLocationManagement', { component: CustomLocationManagementScreen });
registerScreen('ADMIN_CustomLocationManagement', { component: CustomLocationManagementScreen });

// Address Review lives in the operational modules (like CaseReports), not admin — the
// supervisor role that uses it works in CAD/MDT, and the entity Open actions must stay
// within the selected module. Admins reach it from CAD/MDT too (their group has both).
registerScreen('CAD_AddressReview', { component: SupervisorAddressReviewScreen });
registerScreen('MDT_AddressReview', { component: SupervisorAddressReviewScreen });

registerScreen('CAD_CompanyDocuments', { component: CompanyDocsScreen });
registerScreen('MDT_CompanyDocuments', { component: CompanyDocsScreen });
registerScreen('ADMIN_CompanyDocuments', { component: CompanyDocsScreen });

// SOP screens (opened in new window from call card)
const sopGetProps = (params: Record<string, string>) => {
  const paramsStr = params.params || '';
  let natureOfCallCodesWithSop: number[] = [];
  try {
    natureOfCallCodesWithSop = paramsStr.split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n));
  } catch (_e) {
    // validation handled in component
  }
  return { natureOfCallCodesWithSop };
};
registerScreen('CAD_CallCardSop', { component: SopScreen, getProps: sopGetProps });
registerScreen('MDT_CallCardSop', { component: SopScreen, getProps: sopGetProps });
